import { fetchJSON, routeUrl } from '@theme/utils';

/**
 * Progressive enhancement for the product form: adds to cart over the Ajax API instead of a full
 * page load. Without JS, the wrapped `<form action="/cart/add">` still works.
 *
 * Markup: `<product-form><form>…<button type="submit">…<p data-error hidden></p></form></product-form>`
 * One request: the cart drawer section is rendered with the add (`sections`), so the drawer updates and the
 * new count is known without fetching `/cart.js` or the section again.
 * Emits `cart:updated` on `document` with the new item count, total and drawer HTML, then `cart:open` (opens
 * the cart drawer), then `product-form:added` (bubbling, on the host) so the page can show its own feedback.
 */

/** Section id of sections/cart-drawer.liquid (cart-drawer.js imports this module: no import back). */
const DRAWER = 'cart-drawer';

/**
 * What the cart drawer previews for this add, from the form's data (`snippets/cart-preview.liquid`) and
 * its selected variant (`id`) and `quantity`.
 *
 * @param {string | null | undefined} json - Content of `<script data-cart-preview>`.
 * @param {FormData} data - The product form's data.
 * @returns {CartPreview | null}
 */
export function cartPreview(json, data) {
  if (!json) return null;
  /** @type {{ title: string, kind: string, color: string, variants: Record<string, { price: number, title: string, image: string }> }} */
  let preview;
  try {
    preview = JSON.parse(json);
  } catch {
    return null;
  }
  const id = String(data.get('id') ?? '');
  const variant = preview.variants?.[id];
  if (!variant) return null;
  return {
    variantId: Number(id),
    quantity: Math.max(1, Number(data.get('quantity')) || 1),
    title: preview.title,
    meta: [preview.kind, variant.title].filter(Boolean).join(' · '),
    color: preview.color,
    image: variant.image,
    price: variant.price,
  };
}

/**
 * Item count and total of a rendered cart drawer (its `data-count` / `data-total`).
 *
 * @param {string | null | undefined} html - Section Rendering API output of the cart drawer.
 * @returns {{ itemCount: number, total: number } | null}
 */
export function drawerCart(html) {
  if (!html) return null;
  const drawer = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer[data-count]');
  if (!drawer) return null;
  return { itemCount: Number(drawer.getAttribute('data-count')), total: Number(drawer.getAttribute('data-total')) };
}

export class ProductForm extends HTMLElement {
  // Listen on the host (submit bubbles): children may not be parsed yet when connectedCallback runs.
  connectedCallback() {
    this.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.onSubmit);
  }

  /** @param {SubmitEvent} event */
  onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const error = /** @type {HTMLElement | null} */ (form.querySelector('[data-error]'));
    button?.setAttribute('aria-busy', 'true');
    button?.toggleAttribute('disabled', true);
    if (error) error.hidden = true;

    try {
      const body = new FormData(form);
      const item = cartPreview(this.querySelector('script[data-cart-preview]')?.textContent, body);
      body.append('sections', DRAWER);
      body.append('sections_url', window.location.pathname);
      /** @type {Promise<{ sections?: Record<string, string | null> }>} */
      const request = fetchJSON(routeUrl('cart/add.js'), { method: 'POST', body });
      // The drawer shows the line right away (optimistic UI) and rolls back if the request fails.
      if (item) document.dispatchEvent(new CustomEvent('cart:adding', { detail: { item, request } }));
      const added = await request;
      const html = added.sections?.[DRAWER];
      let cart = drawerCart(html);
      if (!cart) {
        /** @type {{ item_count: number, total_price: number }} */
        const json = await fetchJSON(routeUrl('cart.js'));
        cart = { itemCount: json.item_count, total: json.total_price };
      }
      updateCartCount(
        cart.itemCount,
        html ? { total: cart.total, sections: { [DRAWER]: html } } : { total: cart.total },
      );
      document.dispatchEvent(new CustomEvent('cart:open', { detail: null }));
      this.dispatchEvent(
        new CustomEvent('product-form:added', { bubbles: true, detail: { itemCount: cart.itemCount } }),
      );
    } catch (e) {
      if (error) {
        error.textContent = e instanceof Error ? e.message : String(e);
        error.hidden = false;
      }
    } finally {
      button?.removeAttribute('aria-busy');
      button?.toggleAttribute('disabled', false);
    }
  };
}

/**
 * Updates every `[data-cart-count]` badge and notifies listeners (the header makes its badge bounce on
 * `cart:updated`).
 *
 * @param {number} itemCount
 * @param {{ total?: number, sections?: Record<string, string | null> }} [extra] - Cart total in cents (spares
 *   listeners a `/cart.js` request) and freshly rendered sections (the cart drawer renders them directly).
 */
export function updateCartCount(itemCount, extra = {}) {
  for (const el of document.querySelectorAll('[data-cart-count]')) el.textContent = String(itemCount);
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount, ...extra } }));
}

if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
